import { HttpRequest } from '../utils/httpClient';
import { MyPermissionsResponse, PermissionCatalogEntry } from '../features/auth/types';

import { createLogger } from '../utils/logger';
const logger = createLogger('services');

/**
 * Service for the CASL atomic-permission endpoints (Roles module, base path
 * `permissions`).
 */
class PermissionService {
  private httpClient: HttpRequest;

  constructor() {
    const API_BASE_URL = process.env.REACT_APP_API_HOST || 'https://api.dev.app.silia.com';
    const permissionsBaseUrl = `${API_BASE_URL}/permissions`;
    this.httpClient = new HttpRequest(permissionsBaseUrl);
  }

  /**
   * Fetch the current user's GLOBAL atomic permissions (CASL).
   *
   * Backed by GET /permissions/me/permissions. The returned `permissions[].subject`
   * values are the full atomic strings the backend checks (e.g.
   * "agent.process_flows.view"). Superadmin/superuser return a single wildcard
   * entry with subject === "all".
   *
   * Loaded by AuthContext on login and session-restore (see loadAtomicPermissions).
   * Account switching does NOT call this directly — it triggers a full page reload
   * (AccountContext.setAccount → window.location.reload), which re-runs the restore
   * path and reloads permissions for the new account. It drives the `can()` helper.
   *
   * @returns The role name plus the list of atomic permission entries (empty list on failure).
   */
  async getMyPermissions(): Promise<MyPermissionsResponse> {
    try {
      const response = await this.httpClient.get('me/permissions');

      if (response.status !== 200) {
        logger.error(`Failed to fetch my permissions: ${response.status} - ${response.statusText}`);
        return { role: '', permissions: [] };
      }

      const data = response.data as MyPermissionsResponse;
      return { role: data.role ?? '', permissions: data.permissions ?? [] };
    } catch (error) {
      logger.error('Error fetching my permissions:', error);
      return { role: '', permissions: [] };
    }
  }

  /**
   * Fetch the full catalog of atomic permission strings that exist in the system.
   *
   * Backed by GET /permissions/catalog. DB-driven — use this to build the role
   * matrix UI (Fase 2) and tooltips rather than hardcoding an enum in the FE.
   *
   * @returns The catalog entries (empty list on failure).
   */
  async getCatalog(): Promise<PermissionCatalogEntry[]> {
    try {
      const response = await this.httpClient.get('catalog');

      if (response.status !== 200) {
        logger.error(`Failed to fetch permission catalog: ${response.status} - ${response.statusText}`);
        return [];
      }

      const data = response.data as { catalog: PermissionCatalogEntry[] };
      return data.catalog ?? [];
    } catch (error) {
      logger.error('Error fetching permission catalog:', error);
      return [];
    }
  }
}

// Export singleton instance
export const permissionService = new PermissionService();
